import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { MapComponent } from "../contact/components/MapComponent";
import { homeStore } from "@/_zustand/home";
import { useMachine } from "@/_zustand/machine";
import { useMaquinaById, ListaMaquinasByDistance } from "@/hooks/useMaquinas";
import {
  useTiendasVisibles,
  estadoDeQuery,
} from "@/hooks/useTiendasVisibles";
import {
  filtrarSinLicor,
  textoFallbackFavoritas,
  textoListadoVacio,
  TIENDAS_MSG_SIN_FAVORITAS_EMPRESA,
  TIENDAS_MSG_SIN_FAVORITAS_GENERAL,
} from "@/lib/tiendasVisibles";
import * as _ from "lodash";
import Empty from "@/assets/imgs/empty.png";
import { useAuth } from "@/_zustand/auth";

export const StoresCompany = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { tiendaId: storeTiendaId, setStore } = homeStore();
  const { setEmpresaId, setMachine } = useMachine();
  const { getCompanyRoute, companyHomePath } = useCompanyFlow();

  // Prioritize URL param, then store state
  const urlStoreId =
    params.tiendaId ||
    params.idEmpresa ||
    searchParams.get("storeId") ||
    searchParams.get("empresaId");
  const tiendaId = urlStoreId ? Number(urlStoreId) : storeTiendaId;

  useEffect(() => {
    if (urlStoreId) {
      setStore(Number(urlStoreId));
      setEmpresaId(Number(urlStoreId));
    }
  }, [urlStoreId, setStore, setEmpresaId]);

  const { userAddress } = useAuth();
  const latitud = userAddress?.length === 2 ? Number(userAddress[0]) : -33.4369;
  const longitud =
    userAddress?.length === 2 ? Number(userAddress[1]) : -70.6693;

  const {
    data: response,
    refetch,
    isSuccess,
    isError,
  } = ListaMaquinasByDistance(latitud, longitud, tiendaId);
  // Ver Stores.jsx: la query es `enabled:false` + refetch desde un efecto, así
  // que hay que distinguir "cargando"/"error" de "no hay ninguna cerca" para
  // no afirmar lo segundo cuando pasa lo primero.
  const estadoCercanas = estadoDeQuery({ isSuccess, isError });
  // 3. EXTRACCIÓN Y USO DE LA DATA
  // Accedemos a la lista real de máquinas que está anidada en la propiedad 'data'
  const maquinasResponse =
    response && response.status === "success" && Array.isArray(response.data)
      ? response.data
      : [];

  const getMaquinaById = useMaquinaById();

  // Las favoritas ya no se leen acá: las resuelve useTiendasVisibles (validadas
  // contra el padrón de activas y acotadas a la empresa en contexto).
  const [mostrarFavoritas] = useState(false);

  // Mismo callejón sin salida que en Stores.jsx: sin tiendas dentro del radio
  // (`RadioTiendas`, ver storeByDistance.post.ts) la página cortaba con "No
  // hay tiendas cercanas" aunque el usuario tuviera favoritas de la empresa.
  // La lógica y el porqué viven en src/lib/tiendasVisibles.js, compartidos con
  // Stores.jsx — este componente y ese son casi gemelos y ya nos pasó que un
  // fix aterrizara sólo en una de dos copias (ADR-0013).
  const { favoritasDelContexto, tiendasAMostrar, motivoFallback } =
    useTiendasVisibles({
      maquinasCercanas: maquinasResponse,
      tiendaId,
      mostrarFavoritas,
      estadoCercanas,
    });
  const avisoFallback = textoFallbackFavoritas({ motivoFallback, tiendaId });

  useEffect(() => {
    refetch();
  }, [latitud, longitud, tiendaId, refetch]);

  const machineClick = async (machine) => {
    const machineId = machine.maquina_id;
    try {
      // 1. Obtener la respuesta de la máquina
      const resp = await getMaquinaById.mutateAsync(machineId);

      // 2. Validaciones iniciales
      if (_.size(resp) === 0) {
        toast.error("Hay un error con esta máquina.");
        navigate(companyHomePath, { replace: false });
        return;
      }

      const maquinaResp = resp[0]; // Asumimos que siempre habrá un elemento si _size(resp) > 0

      if (maquinaResp.estado_id !== 1) {
        toast.error("Esta máquina no se encuentra activa.");
        navigate(companyHomePath, { replace: false });
        return;
      }
      // 3. Mapear los datos de la máquina
      const maquinaMap = {
        comuna_nombre: maquinaResp.comuna_nombre,
        region_nombre: maquinaResp.region_nombre,
        empresa_id: maquinaResp.empresa_id,
        empresa_nombre: maquinaResp.empresa_nombre,
        empresa_rut: "",
        maquina_direccion: maquinaResp.maquina_direccion,
        maquina_latitud: maquinaResp.maquina_latitud,
        maquina_longitud: maquinaResp.maquina_longitud,
        maquina_nombre: maquinaResp.maquina_nombre,
        maquinamodelo_icono: maquinaResp.maquinamodelo_icono,
        oneclick_apikey: maquinaResp.oneclick_apikey,
        oneclick_codigo_comercio: maquinaResp.oneclick_codigo_comercio,
        oneclick_codigo_tienda: maquinaResp.oneclick_codigo_tienda,
        webpay_apikey: maquinaResp.webpay_apikey,
        webpay_codigo_comercio: maquinaResp.webpay_codigo_comercio,
        maquina_id: maquinaResp.maquina_id,
        vende_licor: maquinaResp.vende_licor,
        maquina_telefono: maquinaResp.maquina_telefono,
      };
      // 4. Asignar al estado y navegar
      setMachine(maquinaMap);
      setStore(maquinaMap.empresa_id);

      // Navegación inmediata después de setMachine
      navigate(getCompanyRoute(`store/${machineId}`), { replace: false });
    } catch (error) {
      console.log(error);
      // Manejo de errores de la llamada al servicio
      console.error("Error al obtener datos de la máquina:", machine);
      toast.error("Ocurrió un error al cargar la información de la máquina.");
      //navigate(`/`, { replace: false }); // Redirigir a home en caso de cualquier error no manejado
    }
  };

  const StoreList = (stores) => {
    // Favoritas ya validadas y acotadas a la empresa en contexto, no el
    // `favorites` crudo (que mezclaba máquinas de otras empresas).
    const tiendas = mostrarFavoritas ? favoritasDelContexto : stores.stores;

    // Ver Stores.jsx: sin favoritas que mostrar, esta rama antes era
    // inalcanzable y el componente devolvía undefined.
    if (mostrarFavoritas && _.size(tiendas) === 0) {
      return (
        <div className="row">
          <p className="text-center text-black px-3 ">
            {Number(tiendaId) > 0
              ? TIENDAS_MSG_SIN_FAVORITAS_EMPRESA
              : TIENDAS_MSG_SIN_FAVORITAS_GENERAL}
          </p>
          <img
            className="img-fluid"
            src={Empty}
            alt="store-empty"
            style={{ objectFit: "scale-down" }}
          />
        </div>
      );
    }

    if (_.size(tiendas) > 0 && tiendaId != 0) {
      return (
        <div className="store-list">
          {filtrarSinLicor(tiendas).map((store, index) => (
            <MachineCard key={store.maquina_id} store={store} index={index} />
          ))}
        </div>
      );
    } else if (_.size(tiendas) > 0 && tiendaId == 0) {
      return (
        <div className="store-list">
          {filtrarSinLicor(_.uniqBy(tiendas, "maquina_id")).map(
            (store, index) => (
              <MachineCard key={store.maquina_id} store={store} index={index} />
            ),
          )}
        </div>
      );
    }
  };

  const MachineCard = (store, index) => {
    const storeCard = store.store;
    const hasOfertaDelDia =
      storeCard.monto_fijo === true || storeCard.porcentaje === true;
    const hasOferta2x1 = storeCard.oferta2x1 === true;

    if (
      storeCard.empresa_id == tiendaId ||
      (tiendaId == 0 && storeCard.vende_licor == 0)
    ) {
      return (
        <div
          key={index}
          className={`store-card ${store.highlighted ? "highlighted" : ""}`}
          onClick={() => machineClick(storeCard)} // Este onClick se mantiene para la tarjeta
          style={{ position: "relative" }} // Necesario para posicionar el corazón si usa absolute
        >
          <img
            src={storeCard.logo_empresa}
            alt={storeCard.empresa_nombre || storeCard.maquina_nombre}
            className="store-image"
          />

          <div className="store-info">
            <h3 className="store-name">{storeCard.maquina_nombre}</h3>{" "}
            <p className="store-location">{storeCard.region_nombre}</p>{" "}
            <p className="store-address">
              📍{" "}
              <span className="distance">
                {storeCard.maquina_direccion || "Dirección no disponible"}
              </span>{" "}
              {storeCard.comuna_nombre}
            </p>
            <div className="store-tags">
              {hasOfertaDelDia && (
                <span className="store-tag">Ofertas del día</span>
              )}
              {hasOferta2x1 && (
                <span className="store-tag store-tag-2x1">2x1</span>
              )}
            </div>
          </div>

          {/* El corazón ahora tiene un onClick directo */}
          {/* <i
            //className="fav-heart"
            className={` ${isFavorite ? "fav-heart-fill" : "fav-heart"}`}
            onClick={handleFavoriteClick}
            style={{
              cursor: "pointer",
            }}
          /> */}
        </div>
      );
    }
  };

  // Sólo se corta cuando de verdad no hay nada: ni tiendas dentro del radio
  // ni favoritas guardadas de esta empresa.
  if (_.size(tiendasAMostrar) == 0 && !mostrarFavoritas) {
    return (
      <section className="container my-5" style={{ flexGrow: 1 }}>
        <div className="row d-flex justify-content-center">
          <h5 className="mt-0 inter-bold about-us-title-1 text-start">
            Tiendas
          </h5>
          <div
            className="col-md-12 col-12 mb-md-0 p-md-4 text-center"
            style={{ minHeight: "400px" }}
          >
            <img className="img-fluid" src={Empty} alt="store-empty" />
            <p className="text-center text-black px-3 text-info">
              {textoListadoVacio({ tiendaId, estadoCercanas })}
              <i className="icon-location"></i>
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="container my-5" style={{ flexGrow: 1 }}>
      <div className="row d-flex justify-content-center">
        <h5 className="mt-0 inter-bold about-us-title-1 text-start">
          {mostrarFavoritas ? "Mis tiendas Favoritas" : "Tiendas"}
        </h5>
        <div className="col-md-4 col-12 mb-md-0 p-md-4 col-max-height">
          {/* <StoreButtons
            mostrarFavoritas={mostrarFavoritas}
            setMostrarFavoritas={setMostrarFavoritas}
          /> */}
          {/* <StoreLogos /> */}
          <div>
            {avisoFallback && (
              <p className="text-black px-1 mb-2" style={{ fontSize: "14px" }}>
                {avisoFallback}
              </p>
            )}
            <StoreList stores={tiendasAMostrar} />
          </div>
        </div>
        <div className="col-md-8 p-2 ps-md-0 col-max-height">
          <MapComponent />
        </div>
      </div>
    </section>
  );
};
